import { Fragment, useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import api from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Search, Loader2, RefreshCw, ExternalLink, Gavel, ChevronLeft, ChevronRight, MapPin, ChevronDown, ChevronUp, Ruler, ArrowUp, ArrowDown, ArrowUpDown, Columns3, Table2, Map as MapIcon, Bell, Plus, X, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const HIDDEN_COLS_KEY = "finderAsteHiddenCols";

// Colore pallino sulla mappa: per categoria immobile, salvo l'override "opportunità"
// (asta deserta) che ha priorità su qualunque categoria — è il segnale di business più
// rilevante per questa piattaforma (potenziale saldo e stralcio), quindi deve risaltare.
const CATEGORY_COLORS = {
  IMMOBILE_RESIDENZIALE: "#3B82F6",
  IMMOBILE_COMMERCIALE: "#F59E0B",
  IMMOBILE_INDUSTRIALE: "#EC4899",
  IMPIANTO_SPORTIVO: "#10B981",
  ALTRA_CATEGORIA: "#A1A1AA",
};
const OPPORTUNITY_COLOR = "#8B5CF6";

const escapeHtml = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const COLUMNS = [
  { id: "categoria", label: "Categoria" },
  { id: "immobile", label: "Immobile", sortKey: "citta" },
  { id: "disponibilita", label: "Disponibilità" },
  { id: "diritto", label: "Diritto" },
  { id: "rge", label: "RGE" },
  { id: "data_vendita", label: "Data vendita / Esito", sortKey: "data_vendita" },
  { id: "pubblicato", label: "Pubblicato", sortKey: "data_pubblicazione" },
  { id: "prezzo_base", label: "Prezzo base", sortKey: "prezzo_base", align: "right" },
  { id: "offerta_minima", label: "Offerta min.", sortKey: "offerta_minima", align: "right" },
  { id: "annuncio", label: "" },
];

// Etichette ufficiali del portale PVP (da /bo-ms/filtriRicercaVendite e i18n dei widget).
export const CAT_LOTTO_LABELS = {
  IMMOBILE_COMMERCIALE: "Immobile Commerciale",
  ALTRA_CATEGORIA: "Altra Categoria",
  IMMOBILE_RESIDENZIALE: "Immobile Residenziale",
  IMPIANTO_SPORTIVO: "Impianto Sportivo",
  IMMOBILE_INDUSTRIALE: "Immobile Industriale",
};

export const CAT_BENE_LABELS = {
  FABBRICATI_AGRICOLI: "Fabbricati per attività agricole",
  NEGOZIO: "Negozio",
  UFFICIO: "Ufficio",
  OPIFICIO_INDUSTRIALE: "Opificio industriale",
  OPIFICI: "Opifici",
  FABBRICATI_COSTRUITI_PER_ESIGENZE_IND: "Fabbricato per esigenze industriali",
  FABBRICATI_E_LOCALI_PER_ESERCIZI_SPORTIVI: "Fabbricato/locale per esercizi sportivi",
  FABBRICATI_E_LOCALI_SPORTIVI_CON_LUCRO: "Fabbricato/locale sportivo con fini di lucro",
  PALESTRA_O_ALTRO_IMMOBILE: "Palestra o altro immobile sportivo",
  ALBERGO_E_PENSIONE: "Albergo e pensione",
  PALCO_TEATRALE: "Palco teatrale",
  LASTRICO_SOLARE: "Lastrico solare",
  LOTTO_EDIFICABILE: "Lotto edificabile",
  CASE_DI_CURA_E_OSPEDALI: "Case di cura e ospedali",
  STABILIMENTI_BALNEARI_CURATIVI: "Stabilimenti balneari e di acque curative",
  STALLE_SCUDERIE_RIMESSE: "Stalle, scuderie, rimesse, autorimesse",
  TETTOIE_CHIUSE_APERTE: "Tettoie chiuse o aperte",
  ALBERGHI_E_PENSIONI: "Alberghi e pensioni",
  TEATRI_CINEMATOGRAFI_SALE: "Teatri, cinematografi, sale per concerti",
  ISTITUTO_CREDITO_CAMBIO: "Istituto di credito, cambio e assicurazioni",
  EDIFICI_GALLEGGIANTI_O_SOSPESI: "Edifici galleggianti o sospesi",
  EDIFICI_A_DESTINAZIONE_PARTICOLARE: "Edificio a destinazione particolare",
  PORZIONE_DI_IMMOBILE: "Porzione di immobile",
  POSTO_AUTO: "Posto auto",
  POZZO: "Pozzo",
  CASA_DI_CURA_O_OSPEDALE: "Casa di cura o ospedale",
  CONVENTO_CONVITTO_O_ISTITUTO_SIMILARE: "Convento, convitto o istituto similare",
  PORZIONE_DI_FABBRICATO: "Porzione di fabbricato in costruzione",
  FABBRICATO_RURALE: "Fabbricato rurale",
  STABILIMENTO_BALNEARE: "Stabilimento balneare",
  TERRENO: "Terreno",
  CANTINA: "Cantina",
  COMPENDIO_PIGNORATO: "Compendio pignorato",
  ABITAZIONE_TIPO_SIGN: "Abitazione tipo signorile",
  ABITAZIONE_TIPO_CIV: "Abitazione tipo civile",
  ABITAZIONE_TIPO_ECO: "Abitazione tipo economico",
  ABITAZIONE_TIPO_POP: "Abitazione tipo popolare",
  ABITAZIONE_TIPO_UPOP: "Abitazione tipo ultrapopolare",
  ABITAZIONE_TIPO_RUR: "Abitazione tipo rurale",
  ABITAZIONE_IN_VILLINI: "Abitazione in villini",
  ABITAZIONE_IN_VILLE: "Abitazione in ville",
  CASTELLI_PALAZZI: "Castelli, palazzi di pregio artistico",
  ABITAZIONI_E_ALL_TIPICI: "Abitazioni e alloggi tipici dei luoghi",
  APPARTAMENTO: "Appartamento",
  FABBRICATO_IN_COSTR: "Fabbricato in costruzione",
  CASTEL_IMM_PREGIO_STR: "Castello, immobile di pregio storico",
  VILLA: "Villa",
  FABBRICATO: "Fabbricato",
  UFFICI_E_STUDI: "Uffici e studi privati",
  DEPOSITO: "Deposito",
  GARAGE_AUTORIMESSA: "Garage o autorimessa",
  LABORATORIO_ARTIGIANO: "Laboratorio artigiano",
  MAGAZZINO: "Magazzino",
  MAGAZZINI_DEPOSITO: "Magazzini sotterranei, deposito",
  NEGOZI_BOTTEGHE: "Negozi, botteghe",
  MAGAZZINI_E_DEPOSITO: "Magazzini e locali di deposito",
  LABORATORI_ARTI: "Laboratori per arti e mestieri",
  FABBRICATI_COMMERCIALI: "Fabbricato per esigenze commerciali",
};

// Codici ufficiali "disponibilità" (SEARCH_ANNUNCI_FORM.DISPONIBILITA_* nel bundle i18n del portale).
export const DISP = {
  LIBER: { label: "Libero", color: "#10B981" },
  OCCUP: { label: "Occupato", color: "#E11D48" },
  OCCST: { label: "Occupato senza titolo", color: "#E11D48" },
  INCOR: { label: "In corso di liberazione", color: "#D97706" },
  NON_SPECIFICATA: { label: "Non specificato", color: "#71717A" },
};

// Codici "esito" non documentati ufficialmente dal portale (nessuna stringa i18n trovata):
// interpretazione basata sulla terminologia standard delle aste giudiziarie italiane.
export const ESITO = {
  ASDES: { label: "Asta deserta", color: "#8B5CF6" },
  AGGIU: { label: "Aggiudicata", color: "#71717A" },
  SOSPE: { label: "Sospesa", color: "#D97706" },
};

export const catLottoLabel = (c) => CAT_LOTTO_LABELS[c] || (c ? c.replaceAll("_", " ") : "—");
export const catBeneLabel = (c) => CAT_BENE_LABELS[c] || (c ? c.replaceAll("_", " ").toLowerCase() : null);
export const mapsUrl = (lat, lng) => `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
const dataVenditaGiorno = (dv) => (dv ? dv.slice(0, 10) : null);

export default function FinderAste() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [capped, setCapped] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [categorie, setCategorie] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [filters, setFilters] = useState({ q: "", citta: "", provincia: "", categoria: "", prezzo_min: "", prezzo_max: "", pub_da: "", pub_a: "", stato: "attive", sort: "data_pubblicazione", dir: "desc" });
  const [expanded, setExpanded] = useState(null);
  const [dettagli, setDettagli] = useState({});
  const [oggi, setOggi] = useState(null);
  const [hiddenCols, setHiddenCols] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(HIDDEN_COLS_KEY) || "[]")); } catch { return new Set(); }
  });
  const [colsMenuOpen, setColsMenuOpen] = useState(false);
  const [view, setView] = useState("tabella");
  const [mapItems, setMapItems] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapMeta, setMapMeta] = useState(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const size = 25;
  const [alerts, setAlerts] = useState([]);
  const [alertQuery, setAlertQuery] = useState("");
  const [alertSelected, setAlertSelected] = useState({ citta: null, provincia: null });
  const [alertSuggestions, setAlertSuggestions] = useState([]);
  const [alertSuggestOpen, setAlertSuggestOpen] = useState(false);
  const [savingAlert, setSavingAlert] = useState(false);
  const [alertMenuOpen, setAlertMenuOpen] = useState(false);

  useEffect(() => { localStorage.setItem(HIDDEN_COLS_KEY, JSON.stringify([...hiddenCols])); }, [hiddenCols]);
  const toggleCol = (id) => setHiddenCols((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const visibleColumns = COLUMNS.filter((c) => !hiddenCols.has(c.id));

  const handleSort = (col) => {
    if (!col.sortKey || filters.q) return;
    setPage(0);
    setFilters((f) => (f.sort === col.sortKey ? { ...f, dir: f.dir === "asc" ? "desc" : "asc" } : { ...f, sort: col.sortKey, dir: "asc" }));
  };

  const toggleDettaglio = async (a) => {
    if (expanded === a.id_annuncio) { setExpanded(null); return; }
    setExpanded(a.id_annuncio);
    if (a.dettaglio_fetched_at || dettagli[a.id_annuncio]) return;
    try {
      const r = await api.get(`/aste/${a.id_annuncio}/dettaglio`);
      setDettagli((d) => ({ ...d, [a.id_annuncio]: r.data }));
    } catch (e) {
      toast.error("Errore nel recupero del dettaglio");
    }
  };

  const buildParams = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v != null));
    params.page = page;
    params.size = size;
    return params;
  }, [filters, page]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/aste", { params: buildParams() })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); setCapped(!!r.data.capped); setOggi(r.data.oggi); })
      .catch(() => toast.error("Errore nel caricamento delle aste"))
      .finally(() => setLoading(false));
  }, [buildParams]);

  const loadStatus = useCallback(() => {
    api.get("/aste/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const loadAlerts = useCallback(() => {
    api.get("/aste/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, []);

  // Debounce: interroga l'autocomplete solo 250ms dopo l'ultima battitura.
  useEffect(() => {
    const q = alertQuery.trim();
    if (q.length < 2 || (alertSelected.citta || alertSelected.provincia)) {
      setAlertSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      api.get("/aste/localita", { params: { q } }).then((r) => setAlertSuggestions(r.data)).catch(() => setAlertSuggestions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [alertQuery, alertSelected]);

  const selectAlertSuggestion = (s) => {
    setAlertQuery(s.label);
    setAlertSelected({ citta: s.citta, provincia: s.provincia });
    setAlertSuggestions([]);
    setAlertSuggestOpen(false);
  };

  const createAlert = async (e) => {
    e.preventDefault();
    // Se non è stato scelto un suggerimento disambiguato, il testo libero è interpretato come città.
    const citta = alertSelected.citta || (alertSelected.provincia ? null : alertQuery.trim() || null);
    const provincia = alertSelected.provincia || null;
    if (!citta && !provincia) {
      toast.error("Indica una città o una provincia");
      return;
    }
    setSavingAlert(true);
    try {
      await api.post("/aste/alerts", { citta, provincia });
      toast.success("Alert creato: riceverai una email alle 8:00 se vengono trovati nuovi immobili");
      setAlertQuery("");
      setAlertSelected({ citta: null, provincia: null });
      loadAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione dell'alert");
    } finally {
      setSavingAlert(false);
    }
  };

  const deleteAlert = async (id) => {
    try {
      await api.delete(`/aste/alerts/${id}`);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch {
      toast.error("Errore nella rimozione dell'alert");
    }
  };

  const buildMapParams = useCallback(() => {
    const { sort, dir, ...rest } = filters;
    return Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== "" && v != null));
  }, [filters]);

  const loadMap = useCallback(() => {
    setMapLoading(true);
    api.get("/aste/mappa", { params: buildMapParams() })
      .then((r) => { setMapItems(r.data.items); setMapMeta(r.data); })
      .catch(() => toast.error("Errore nel caricamento della mappa"))
      .finally(() => setMapLoading(false));
  }, [buildMapParams]);

  useEffect(() => { if (view === "mappa") loadMap(); }, [view, loadMap]);

  // Init/distruzione della mappa Leaflet: solo quando la vista è attiva, così il
  // contenitore #finder-aste-map esiste già nel DOM quando Leaflet vi si aggancia.
  useEffect(() => {
    if (view !== "mappa") return;
    const m = L.map("finder-aste-map", { center: [42.5, 12.5], zoom: 6, scrollWheelZoom: true });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: "&copy; OpenStreetMap &copy; CARTO", maxZoom: 19,
    }).addTo(m);
    const cluster = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 55,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      iconCreateFunction: (c) => {
        const count = c.getChildCount();
        const size = count < 10 ? 30 : count < 100 ? 38 : count < 1000 ? 46 : 54;
        return L.divIcon({
          html: `<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;border-radius:50%;background:rgba(234,123,43,0.88);border:2px solid #0A1826;color:#0A1826;font-weight:700;font-size:${count < 100 ? 12 : 11}px">${count}</div>`,
          className: "finder-cluster-icon",
          iconSize: L.point(size, size),
        });
      },
    });
    m.addLayer(cluster);
    mapRef.current = m;
    clusterRef.current = cluster;
    setTimeout(() => m.invalidateSize(), 80);
    return () => {
      m.remove();
      mapRef.current = null;
      clusterRef.current = null;
    };
  }, [view]);

  // Disegno dei pallini: si ridisegna a ogni nuovo set di risultati filtrati.
  useEffect(() => {
    const cluster = clusterRef.current;
    if (!cluster || view !== "mappa") return;
    cluster.clearLayers();
    const pts = [];
    mapItems.forEach((a) => {
      if (a.lat == null || a.lng == null) return;
      const opportunita = a.esito === "ASDES";
      const color = opportunita ? OPPORTUNITY_COLOR : (CATEGORY_COLORS[a.categoria_lotto] || CATEGORY_COLORS.ALTRA_CATEGORIA);
      const marker = L.circleMarker([a.lat, a.lng], { radius: 7, color, weight: 1.5, fillColor: color, fillOpacity: 0.75 });
      marker.bindPopup(buildPopupHtml(a), { maxWidth: 280 });
      marker.on("popupopen", (e) => wirePopupDettaglio(e, a));
      cluster.addLayer(marker);
      pts.push([a.lat, a.lng]);
    });
    if (pts.length && mapRef.current) {
      try { mapRef.current.fitBounds(pts, { padding: [40, 40], maxZoom: 12 }); } catch { /* noop */ }
    }
  }, [mapItems, view]);

  const buildPopupHtml = (a) => {
    const catColor = CATEGORY_COLORS[a.categoria_lotto] || CATEGORY_COLORS.ALTRA_CATEGORIA;
    const opportunita = a.esito === "ASDES";
    const esitoInfo = ESITO[a.esito];
    const dispInfo = DISP[(a.disponibilita || [])[0]];
    const lottoLabel = a.numero_lotto === "LOTTO UNICO" ? "Lotto Unico" : (a.numero_lotto ? `Lotto ${a.numero_lotto}` : "—");
    const rge = `${a.procedura ?? "—"}${a.anno_rg ? "/" + a.anno_rg : ""} - ${escapeHtml(lottoLabel)}`;
    const dataV = a.data_vendita ? new Date(a.data_vendita).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
    return `
      <div style="min-width:220px">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:3px">
          <span style="width:8px;height:8px;border-radius:50%;background:${opportunita ? OPPORTUNITY_COLOR : catColor};display:inline-block;flex-shrink:0"></span>
          <strong>${escapeHtml(catLottoLabel(a.categoria_lotto))}</strong>
        </div>
        <div>${escapeHtml(a.citta)}${a.provincia && a.provincia !== a.citta ? " (" + escapeHtml(a.provincia) + ")" : ""}</div>
        <div style="color:#888;font-size:11px;margin-bottom:4px">${escapeHtml(a.indirizzo)}</div>
        <div style="font-size:11px">RGE ${rge}</div>
        <div style="color:#888;font-size:10px;margin-bottom:6px">${escapeHtml(a.tribunale)}</div>
        <div style="margin-bottom:6px">
          ${dispInfo ? `<span style="font-size:9px;text-transform:uppercase;padding:1px 6px;border-radius:2px;border:1px solid ${dispInfo.color}88;background:${dispInfo.color}22;color:${dispInfo.color};margin-right:4px">${dispInfo.label}</span>` : ""}
          ${esitoInfo ? `<span style="font-size:9px;text-transform:uppercase;padding:1px 6px;border-radius:2px;border:1px solid ${esitoInfo.color}88;background:${esitoInfo.color}22;color:${esitoInfo.color}">${esitoInfo.label}</span>` : ""}
        </div>
        <div style="font-size:12px">Prezzo base <b>${eur(a.prezzo_base, 2)}</b></div>
        <div style="font-size:12px;margin-bottom:2px">Offerta min. <b>${eur(a.offerta_minima, 2)}</b>${a.rialzo_minimo != null ? ` <span style="color:#888">(rialzo ${eur(a.rialzo_minimo, 2)})</span>` : ""}</div>
        <div style="font-size:12px;margin-bottom:8px">Vendita ${dataV}</div>
        <div class="map-dett-slot" style="font-size:11px;color:#888;margin-bottom:8px">
          <button class="map-dett-btn" style="background:transparent;border:1px solid rgba(234,123,43,.6);color:#EA7B2B;font-size:10px;padding:2px 8px;border-radius:2px;cursor:pointer">Superficie, vani, piano, catasto</button>
        </div>
        <div style="display:flex;gap:12px">
          <a href="${a.url_annuncio}" target="_blank" rel="noopener noreferrer" style="color:#EA7B2B;font-size:11px;text-decoration:none">Annuncio ↗</a>
          <a href="https://www.google.com/maps/search/?api=1&query=${a.lat},${a.lng}" target="_blank" rel="noopener noreferrer" style="color:#888;font-size:11px;text-decoration:none">Maps ↗</a>
        </div>
      </div>
    `;
  };

  const wirePopupDettaglio = (e, a) => {
    const el = e.popup.getElement();
    const slot = el?.querySelector(".map-dett-slot");
    const btn = slot?.querySelector(".map-dett-btn");
    if (!btn || btn.dataset.wired) return;
    btn.dataset.wired = "1";
    btn.addEventListener("click", async () => {
      btn.disabled = true;
      btn.textContent = "Recupero…";
      try {
        const r = await api.get(`/aste/${a.id_annuncio}/dettaglio`);
        const d = r.data;
        const catasto = (d.dati_catastali || [])
          .map((c) => [c.textFoglio && `F.${c.textFoglio}`, c.textParticella && `P.${c.textParticella}`, c.textSubalterno && `Sub.${c.textSubalterno}`].filter(Boolean).join(" "))
          .join(" · ") || "n.d.";
        slot.innerHTML = `Superficie: <b>${d.superficie ? escapeHtml(d.superficie) + " mq" : "n.d."}</b> · Vani: <b>${d.numero_vani ?? "n.d."}</b> · Piano: <b>${escapeHtml(d.piano) || "n.d."}</b><br/>Catasto: ${escapeHtml(catasto)}`;
      } catch {
        btn.disabled = false;
        btn.textContent = "Riprova";
      }
    });
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadAlerts(); }, [loadAlerts]);
  useEffect(() => { api.get("/aste/categorie").then((r) => setCategorie(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!status?.running && !syncing) return;
    const t = setInterval(() => { loadStatus(); load(); }, 5000);
    return () => clearInterval(t);
  }, [status?.running, syncing, loadStatus, load]);

  const set = (k, v) => { setPage(0); setFilters((f) => ({ ...f, [k]: v })); };
  const selectCls = "bg-[#122A3F] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]";
  const inputCls = "bg-[#0A1826] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]";

  const startSync = async (mode) => {
    setSyncing(true);
    try {
      await api.post("/aste/sync", { mode });
      toast.success(mode === "backfill" ? "Caricamento completo avviato (richiede tempo)" : "Sincronizzazione avviata");
      setTimeout(loadStatus, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'avvio della sincronizzazione");
    } finally {
      setSyncing(false);
    }
  };

  const [fixingCoord, setFixingCoord] = useState(false);
  const fixCoordinate = async () => {
    setFixingCoord(true);
    try {
      await api.post("/aste/fix-coordinate");
      toast.success("Correzione coordinate avviata in background (può richiedere qualche minuto)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'avvio della correzione");
    } finally {
      setFixingCoord(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / size));
  const canSync = user?.role === "admin" || user?.role === "analista";
  const lastRun = status?.last_run;

  return (
    <div className="space-y-6" data-testid="finder-aste-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-light text-white">Finder Aste</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {status?.totale_annunci != null ? `${status.totale_annunci.toLocaleString("it-IT")} annunci nel database · ` : ""}
            fonte: Portale Vendite Pubbliche (pvp.giustizia.it)
          </p>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <div className="relative">
            <button onClick={() => setAlertMenuOpen((o) => !o)} data-testid="aste-alert-btn" className="flex items-center gap-2 bg-[#122A3F] border border-white/15 text-white text-sm font-medium px-3.5 py-2.5 rounded-sm hover:border-[#EA7B2B]/60 transition-colors">
              <Bell className="w-4 h-4 text-[#EA7B2B]" /> Alert
              {alerts.length > 0 && (
                <span className="text-[10px] leading-none bg-[#EA7B2B] text-black rounded-full px-1.5 py-0.5">{alerts.length}</span>
              )}
            </button>
            {alertMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setAlertMenuOpen(false)} />
                <div data-testid="aste-alert-menu" className="absolute left-0 mt-1 z-20 bg-[#122A3F] border border-white/15 rounded-sm shadow-xl p-3 w-72 space-y-3">
                  <div className="text-xs text-zinc-500">Email alle 8:00 se vengono trovati nuovi immobili nella zona.</div>
                  <form onSubmit={createAlert} className="flex flex-col gap-2">
                    <div className="relative">
                      <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Città / Provincia</label>
                      <input
                        value={alertQuery}
                        onChange={(e) => { setAlertQuery(e.target.value); setAlertSelected({ citta: null, provincia: null }); setAlertSuggestOpen(true); }}
                        onFocus={() => setAlertSuggestOpen(true)}
                        onBlur={() => setTimeout(() => setAlertSuggestOpen(false), 150)}
                        placeholder="Es. Milano"
                        autoComplete="off"
                        data-testid="aste-alert-localita"
                        className={`${inputCls} w-full`}
                      />
                      {alertSuggestOpen && alertSuggestions.length > 0 && (
                        <div data-testid="aste-alert-suggestions" className="absolute left-0 right-0 mt-1 z-30 bg-[#0A1826] border border-white/15 rounded-sm shadow-xl max-h-48 overflow-y-auto">
                          {alertSuggestions.map((s, i) => (
                            <button
                              key={`${s.citta || ""}-${s.provincia || ""}`}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => selectAlertSuggestion(s)}
                              data-testid={`aste-alert-suggestion-${i}`}
                              className="block w-full text-left px-3 py-1.5 text-sm text-zinc-200 hover:bg-white/5 transition-colors"
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button type="submit" disabled={savingAlert} data-testid="aste-alert-create-btn" className="flex items-center justify-center gap-1.5 bg-[#EA7B2B] text-black text-sm font-medium px-3 py-2 rounded-sm hover:bg-[#F59E4A] transition-colors disabled:opacity-40">
                      <Plus className="w-4 h-4" /> Crea alert
                    </button>
                  </form>
                  {alerts.length > 0 ? (
                    <div className="flex flex-col gap-1.5 max-h-48 overflow-y-auto" data-testid="aste-alerts-list">
                      {alerts.map((a) => (
                        <div key={a.id} data-testid={`aste-alert-chip-${a.id}`} className="flex items-center justify-between gap-2 bg-[#0A1826] border border-white/10 rounded-sm px-2.5 py-1.5 text-xs text-zinc-300">
                          <span className="truncate">{a.label || [a.citta, a.provincia].filter(Boolean).join(" / ")}</span>
                          <button onClick={() => deleteAlert(a.id)} data-testid={`aste-alert-remove-${a.id}`} title="Rimuovi alert" className="text-zinc-500 hover:text-rose-400 transition-colors shrink-0">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-zinc-600" data-testid="aste-alerts-empty">Nessun alert impostato.</div>
                  )}
                </div>
              </>
            )}
          </div>
          {canSync && (
            <>
              <button onClick={() => startSync("incremental")} disabled={syncing || status?.running} data-testid="aste-sync-btn" className="flex items-center gap-2 bg-[#EA7B2B] text-black text-sm font-medium px-4 py-2.5 rounded-sm hover:bg-[#F59E4A] transition-colors disabled:opacity-40">
                {status?.running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {status?.running ? "Sincronizzazione in corso…" : "Sincronizza nuovi annunci"}
              </button>
              {user?.role === "admin" && (
                <button onClick={() => startSync("backfill")} disabled={syncing || status?.running} data-testid="aste-backfill-btn" title="Scarica l'intero archivio PVP (richiede tempo)" className="flex items-center gap-2 bg-[#122A3F] border border-white/15 text-white text-sm font-medium px-3.5 py-2.5 rounded-sm hover:border-[#EA7B2B]/60 transition-colors disabled:opacity-40">
                  <Gavel className="w-4 h-4 text-[#EA7B2B]" /> Caricamento completo
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {lastRun && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500" data-testid="aste-last-run">
          <span>Ultima sincronizzazione: <span className="text-zinc-300">{lastRun.started_at ? new Date(lastRun.started_at).toLocaleString("it-IT") : "—"}</span></span>
          <span>Esito: <span className={lastRun.status === "ok" ? "text-emerald-400" : lastRun.status === "running" ? "text-[#EA7B2B]" : "text-rose-400"}>{lastRun.status === "ok" ? "completata" : lastRun.status === "running" ? "in corso" : "errore"}</span></span>
          {lastRun.status === "running" && lastRun.total_pages != null && (
            <span>Avanzamento: <span className="text-[#EA7B2B]">pagina {lastRun.pages ?? 0} di {lastRun.total_pages?.toLocaleString("it-IT")}</span></span>
          )}
          <span>Nuovi: <span className="text-zinc-300">{lastRun.inserted ?? 0}</span></span>
          <span>Aggiornati: <span className="text-zinc-300">{lastRun.updated ?? 0}</span></span>
          {status?.next_run && <span>Prossima: <span className="text-zinc-300">{new Date(status.next_run).toLocaleString("it-IT")}</span></span>}
        </div>
      )}

      {/* Filters */}
      <div className="bg-[#122A3F] border border-white/10 rounded-sm p-4 flex flex-wrap items-center gap-3" data-testid="aste-filters">
        <div className="flex items-center gap-2 bg-[#0A1826] border border-white/10 rounded-sm px-3 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-zinc-500" />
          <input value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Cerca città, indirizzo, descrizione, tribunale…" data-testid="aste-search" className="bg-transparent py-2 text-sm text-white focus:outline-none flex-1" />
        </div>
        <input value={filters.citta} onChange={(e) => set("citta", e.target.value)} placeholder="Città" data-testid="aste-filter-citta" className={`${inputCls} w-36`} />
        <input value={filters.provincia} onChange={(e) => set("provincia", e.target.value)} placeholder="Provincia" data-testid="aste-filter-provincia" className={`${inputCls} w-32`} />
        <select value={filters.categoria} onChange={(e) => set("categoria", e.target.value)} data-testid="aste-filter-categoria" className={selectCls}>
          <option value="">Tutte le categorie</option>
          {categorie.map((c) => <option key={c} value={c}>{catLottoLabel(c)}</option>)}
        </select>
        <select value={filters.stato} onChange={(e) => set("stato", e.target.value)} data-testid="aste-filter-stato" title="Solo attive: data vendita futura. Andate deserte: nessuna offerta ricevuta, opportunità di saldo e stralcio. Esito sconosciuto: scadute ma il tribunale non ha ancora comunicato l'esito." className={selectCls}>
          <option value="attive">Solo aste attive</option>
          <option value="deserte">Andate deserte (opportunità)</option>
          <option value="aggiudicate">Aggiudicate (vendute)</option>
          <option value="sconosciute">Esito sconosciuto</option>
          <option value="tutte">Tutte (incl. scadute)</option>
        </select>
        <input type="number" value={filters.prezzo_min} onChange={(e) => set("prezzo_min", e.target.value)} placeholder="Offerta min. da €" title="Filtra sull'offerta minima richiesta per partecipare" data-testid="aste-filter-prezzo-min" className={`${inputCls} w-36`} />
        <input type="number" value={filters.prezzo_max} onChange={(e) => set("prezzo_max", e.target.value)} placeholder="Offerta min. a €" title="Filtra sull'offerta minima richiesta per partecipare" data-testid="aste-filter-prezzo-max" className={`${inputCls} w-36`} />
        <div className="flex items-center gap-1 bg-[#122A3F] border border-white/10 rounded-sm px-2" title="Pubblicati dal">
          <span className="text-xs text-zinc-500">Pubbl. dal</span>
          <input type="date" value={filters.pub_da} onChange={(e) => set("pub_da", e.target.value)} data-testid="aste-filter-pub-da" className="bg-transparent py-2 text-sm text-white focus:outline-none" />
        </div>
        <div className="flex items-center gap-1 bg-[#122A3F] border border-white/10 rounded-sm px-2" title="Pubblicati fino al">
          <span className="text-xs text-zinc-500">al</span>
          <input type="date" value={filters.pub_a} onChange={(e) => set("pub_a", e.target.value)} data-testid="aste-filter-pub-a" className="bg-transparent py-2 text-sm text-white focus:outline-none" />
        </div>
      </div>

      {/* View toggle + column picker */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-[#122A3F] border border-white/15 rounded-sm p-0.5">
            <button onClick={() => setView("tabella")} data-testid="aste-view-tabella" className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-sm transition-colors ${view === "tabella" ? "bg-[#EA7B2B] text-black" : "text-zinc-400 hover:text-white"}`}>
              <Table2 className="w-3.5 h-3.5" /> Tabella
            </button>
            <button onClick={() => setView("mappa")} data-testid="aste-view-mappa" className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-sm transition-colors ${view === "mappa" ? "bg-[#EA7B2B] text-black" : "text-zinc-400 hover:text-white"}`}>
              <MapIcon className="w-3.5 h-3.5" /> Mappa
            </button>
          </div>
          <span className="text-xs text-zinc-500">
            {view === "tabella"
              ? (filters.q ? "Risultati ordinati per rilevanza" : "Clicca un'intestazione di colonna per ordinare")
              : (mapMeta ? `${(mapMeta.total_geolocalizzati ?? mapMeta.limit).toLocaleString("it-IT")}${mapMeta.capped ? "+" : ""} annunci geolocalizzati sulla mappa${mapMeta.esclusi_coordinate_implausibili ? ` · ${mapMeta.esclusi_coordinate_implausibili.toLocaleString("it-IT")} esclusi per coordinate non plausibili` : ""}` : "")}
          </span>
        </div>
        {view === "tabella" && (
          <div className="relative">
            <button onClick={() => setColsMenuOpen((o) => !o)} data-testid="aste-cols-btn" className="flex items-center gap-2 bg-[#122A3F] border border-white/15 text-zinc-300 text-xs font-medium px-3 py-2 rounded-sm hover:border-[#EA7B2B]/60 transition-colors">
              <Columns3 className="w-3.5 h-3.5" /> Colonne
            </button>
            {colsMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setColsMenuOpen(false)} />
                <div data-testid="aste-cols-menu" className="absolute right-0 mt-1 z-20 bg-[#122A3F] border border-white/15 rounded-sm shadow-xl py-2 w-56">
                  {COLUMNS.filter((c) => c.label).map((c) => (
                    <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-300 hover:bg-white/5 cursor-pointer">
                      <input type="checkbox" checked={!hiddenCols.has(c.id)} onChange={() => toggleCol(c.id)} data-testid={`aste-col-toggle-${c.id}`} className="accent-[#EA7B2B]" />
                      {c.label}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {view === "mappa" && (
        <div className="space-y-3" data-testid="aste-map-view">
          {mapMeta?.capped && (
            <div className="text-xs px-3 py-2 rounded-sm border" style={{ color: "#D97706", borderColor: "#D9770655", backgroundColor: "#D9770615" }}>
              Mostrati i primi {mapMeta.limit.toLocaleString("it-IT")} annunci geolocalizzati — restringi i filtri per vederli tutti sulla mappa.
            </div>
          )}
          <div className="relative rounded-sm overflow-hidden border border-white/10" style={{ height: "600px" }}>
            {mapLoading && <div className="absolute inset-0 z-[500] flex items-center justify-center bg-[#0A1826]/60"><Loader2 className="w-8 h-8 text-[#EA7B2B] animate-spin" /></div>}
            {!mapLoading && mapItems.length === 0 && (
              <div className="absolute inset-0 z-[500] flex flex-col items-center justify-center gap-3 bg-[#0A1826]/70 text-zinc-400">
                <MapPin className="w-8 h-8" /> Nessun annuncio geolocalizzato con questi filtri.
              </div>
            )}
            <div id="finder-aste-map" style={{ height: "100%", width: "100%" }} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-xs text-zinc-400 bg-[#122A3F] border border-white/10 rounded-sm px-4 py-2.5" data-testid="aste-map-legend">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="text-zinc-500 uppercase tracking-wider text-[10px]">Legenda</span>
              {Object.entries(CATEGORY_COLORS).map(([k, color]) => (
                <span key={k} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: color }} />{catLottoLabel(k)}</span>
              ))}
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: OPPORTUNITY_COLOR }} />Asta deserta (opportunità)</span>
            </div>
            {canSync && (
              <button onClick={fixCoordinate} disabled={fixingCoord} data-testid="aste-fix-coord-btn" title="Ricalcola da indirizzo/città le coordinate a 0,0 o fuori dai confini italiani" className="flex items-center gap-1.5 text-zinc-400 hover:text-[#EA7B2B] transition-colors disabled:opacity-40">
                {fixingCoord ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Correggi coordinate anomale
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      {view === "tabella" && (
      <div className="bg-[#122A3F] border border-white/10 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0A1826]/90 backdrop-blur-md">
              <tr className="text-left text-xs text-zinc-500 uppercase tracking-wider border-b border-white/10">
                {visibleColumns.map((col) => {
                  const sortable = !!col.sortKey && !filters.q;
                  const active = col.sortKey && filters.sort === col.sortKey && !filters.q;
                  return (
                    <th key={col.id} onClick={() => handleSort(col)} data-testid={`aste-th-${col.id}`}
                        title={col.sortKey ? (filters.q ? "Non disponibile durante la ricerca libera (risultati per rilevanza)" : `Ordina per ${col.label}`) : undefined}
                        className={`px-4 py-3 font-medium ${col.align === "right" ? "text-right" : ""} ${col.sortKey ? "select-none" : ""} ${sortable ? "cursor-pointer hover:text-white" : col.sortKey ? "cursor-not-allowed opacity-40" : ""}`}>
                      <span className={`inline-flex items-center gap-1 ${col.align === "right" ? "flex-row-reverse" : ""}`}>
                        {col.label}
                        {col.sortKey && !filters.q && (active ? (filters.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />)}
                      </span>
                    </th>
                  );
                })}
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={visibleColumns.length + 1} className="py-16 text-center"><Loader2 className="w-6 h-6 text-[#EA7B2B] animate-spin mx-auto" /></td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={visibleColumns.length + 1} className="py-16 text-center text-zinc-500">
                  {total === 0 && !filters.q && !filters.citta ? "Nessun annuncio nel database — avvia una sincronizzazione" : "Nessun annuncio trovato con questi filtri"}
                </td></tr>
              )}
              {!loading && items.map((a) => {
                const beneLabel = catBeneLabel((a.categoria_bene || [])[0]);
                const disp = (a.disponibilita || [])[0];
                const dispInfo = DISP[disp];
                const isOpen = expanded === a.id_annuncio;
                const dett = dettagli[a.id_annuncio] || (a.dettaglio_fetched_at ? a : null);
                const scaduta = oggi && dataVenditaGiorno(a.data_vendita) && dataVenditaGiorno(a.data_vendita) < oggi;
                // Scaduta senza esito comunicato: incognita, da non confondere con "deserta" (confermata) o "aggiudicata".
                const esitoInfo = ESITO[a.esito] || (scaduta && !a.esito ? { label: "Esito sconosciuto", color: "#52525B" } : null);
                const opportunita = a.esito === "ASDES";
                const rowCls = ["border-b border-white/5 hover:bg-white/5 transition-colors", opportunita ? "bg-[#8B5CF6]/[0.06]" : "", scaduta && !opportunita ? "opacity-40" : ""].filter(Boolean).join(" ");
                const lottoLabel = a.numero_lotto === "LOTTO UNICO" ? "Lotto Unico" : (a.numero_lotto ? `Lotto ${a.numero_lotto}` : "—");
                const cells = {
                  categoria: (
                    <td className="px-4 py-3">
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border text-[#EA7B2B] border-[#EA7B2B]/30 bg-[#EA7B2B]/10 whitespace-nowrap">{catLottoLabel(a.categoria_lotto)}</span>
                      {beneLabel && <div className="text-[10px] text-zinc-500 mt-1 capitalize">{beneLabel}</div>}
                    </td>
                  ),
                  immobile: (
                    <td className="px-4 py-3 max-w-[220px]">
                      <div className="text-white flex items-center gap-1.5">
                        {a.citta}{a.provincia && a.provincia !== a.citta ? ` (${a.provincia})` : ""}
                        {a.lat != null && a.lng != null && (
                          <a href={mapsUrl(a.lat, a.lng)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Apri in Google Maps" data-testid={`asta-maps-${a.id_annuncio}`} className="text-zinc-500 hover:text-[#EA7B2B] transition-colors shrink-0">
                            <MapPin className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 truncate" title={a.indirizzo}>{a.indirizzo}</div>
                      {a.descrizione && <div className="text-xs text-zinc-600 truncate" title={a.descrizione}>{a.descrizione}</div>}
                    </td>
                  ),
                  disponibilita: (
                    <td className="px-4 py-3">
                      {dispInfo ? (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border whitespace-nowrap" style={{ color: dispInfo.color, borderColor: `${dispInfo.color}4d`, backgroundColor: `${dispInfo.color}1a` }}>{dispInfo.label}</span>
                      ) : "—"}
                    </td>
                  ),
                  diritto: (
                    <td className="px-4 py-3 text-xs whitespace-nowrap" title={a.diritto_proprieta === "?" ? "Nella descrizione compaiono più diritti diversi: verificare l'annuncio" : undefined}>
                      <span className={a.diritto_proprieta === "?" ? "text-amber-400" : a.diritto_proprieta === "-" || !a.diritto_proprieta ? "text-zinc-600" : "text-zinc-300"}>
                        {a.diritto_proprieta || "-"}
                      </span>
                    </td>
                  ),
                  rge: (
                    <td className="px-4 py-3 text-zinc-400 font-mono text-xs whitespace-nowrap" title={!a.anno_rg ? "Anno RG disponibile dopo l'apertura del dettaglio" : undefined}>
                      {a.procedura}{a.anno_rg ? `/${a.anno_rg}` : ""} - {lottoLabel}
                      {a.tribunale && <div className="text-[9px] text-zinc-600 mt-0.5 font-sans normal-case truncate max-w-[160px]" title={a.tribunale}>{a.tribunale}</div>}
                    </td>
                  ),
                  data_vendita: (
                    <td className="px-4 py-3 text-zinc-300 font-mono text-xs whitespace-nowrap">
                      {a.data_vendita ? new Date(a.data_vendita).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      {esitoInfo && (
                        <div className="mt-1">
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border whitespace-nowrap" style={{ color: esitoInfo.color, borderColor: `${esitoInfo.color}4d`, backgroundColor: `${esitoInfo.color}1a` }}>{esitoInfo.label}</span>
                        </div>
                      )}
                    </td>
                  ),
                  pubblicato: (
                    <td className="px-4 py-3 text-zinc-400 font-mono text-xs whitespace-nowrap">{dateIt(a.data_pubblicazione)}</td>
                  ),
                  prezzo_base: (
                    <td className="px-4 py-3 text-right font-mono text-zinc-300 whitespace-nowrap">{eur(a.prezzo_base, 2)}</td>
                  ),
                  offerta_minima: (
                    <td className="px-4 py-3 text-right font-mono text-zinc-500 whitespace-nowrap">
                      {eur(a.offerta_minima, 2)}
                      {a.rialzo_minimo != null && <div className="text-[9px] text-zinc-600 mt-0.5">rialzo min: {eur(a.rialzo_minimo, 2)}</div>}
                    </td>
                  ),
                  annuncio: (
                    <td className="px-4 py-3">
                      <a href={a.url_annuncio} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`asta-link-${a.id_annuncio}`} title="Vai all'annuncio su pvp.giustizia.it" className="inline-flex items-center gap-1 text-xs text-[#EA7B2B] hover:text-[#F59E4A] whitespace-nowrap">
                        Annuncio <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </td>
                  ),
                };
                return (
                <Fragment key={a.id_annuncio}>
                <tr data-testid={`asta-row-${a.id_annuncio}`} onClick={() => navigate(`/finder-aste/${a.id_annuncio}`)} title="Apri scheda dettaglio" className={`${rowCls} cursor-pointer`}>
                  {visibleColumns.map((col) => <Fragment key={col.id}>{cells[col.id]}</Fragment>)}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <Link to={`/finder-aste/${a.id_annuncio}`} onClick={(e) => e.stopPropagation()} data-testid={`asta-dettaglio-link-${a.id_annuncio}`} title="Apri scheda dettaglio" className="text-zinc-500 hover:text-[#EA7B2B] transition-colors">
                        <Eye className="w-4 h-4" />
                      </Link>
                      <button onClick={(e) => { e.stopPropagation(); toggleDettaglio(a); }} data-testid={`asta-toggle-${a.id_annuncio}`} title="Superficie, vani, piano, dati catastali" className="text-zinc-500 hover:text-[#EA7B2B] transition-colors">
                        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="bg-[#0A1826]/60 border-b border-white/5" data-testid={`asta-dettaglio-${a.id_annuncio}`}>
                    <td colSpan={visibleColumns.length + 1} className="px-4 py-3">
                      {!dett ? (
                        <div className="flex items-center gap-2 text-xs text-zinc-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Recupero dettaglio dal portale…</div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-xs">
                          <span className="flex items-center gap-1.5 text-zinc-300"><Ruler className="w-3.5 h-3.5 text-[#EA7B2B]" /> Superficie: <span className="font-mono">{dett.superficie ? `${dett.superficie} mq` : "n.d."}</span></span>
                          <span className="text-zinc-300">Vani: <span className="font-mono">{dett.numero_vani ?? "n.d."}</span></span>
                          <span className="text-zinc-300">Piano: <span className="font-mono capitalize">{dett.piano || "n.d."}</span></span>
                          {(dett.dati_catastali || []).length > 0 ? (
                            <span className="text-zinc-300">Catasto: {dett.dati_catastali.map((c, i) => (
                              <span key={i} className="font-mono text-zinc-400">
                                {[c.textFoglio && `F.${c.textFoglio}`, c.textParticella && `P.${c.textParticella}`, c.textSubalterno && `Sub.${c.textSubalterno}`].filter(Boolean).join(" ")}
                                {i < dett.dati_catastali.length - 1 ? " · " : ""}
                              </span>
                            ))}</span>
                          ) : <span className="text-zinc-600">Dati catastali non disponibili</span>}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 text-sm" data-testid="aste-pagination">
          <span className="text-zinc-500 text-xs">{total.toLocaleString("it-IT")}{capped ? "+" : ""} annunci · pagina {page + 1} di {totalPages.toLocaleString("it-IT")}{capped ? "+" : ""}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} data-testid="aste-prev" className="flex items-center gap-1 px-3 py-1.5 rounded-sm border border-white/10 text-zinc-300 hover:bg-white/5 disabled:opacity-30 transition-colors">
              <ChevronLeft className="w-4 h-4" /> Prec
            </button>
            <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} data-testid="aste-next" className="flex items-center gap-1 px-3 py-1.5 rounded-sm border border-white/10 text-zinc-300 hover:bg-white/5 disabled:opacity-30 transition-colors">
              Succ <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
